import { useForm } from "react-hook-form";
import type { SignUpData, TipoDeConta } from "types/auth";
import "styles/cadastro.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const onlyDigits = (v: string) => String(v || "").replace(/\D/g, "");

type FormData = {
  razaoSocial: string;
  email: string;
  tipoDeConta: TipoDeConta;
  senha: string;
  confirmarSenha: string;
  telefoneCelular: string;
};

type Props = { data: SignUpData; onNext: (p: Partial<SignUpData>) => void };

export default function StepAccount({ data, onNext }: Props) {
  const { t } = useTranslation();
  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: {
      razaoSocial: data.razaoSocial,
      email: data.email,
      tipoDeConta: data.tipoDeConta === "parceiro" ? "parceiro" : "empresa",
      senha: data.senha,
      telefoneCelular: data.telefoneCelular,
      confirmarSenha: data.confirmarSenha,
    },
  });

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const onSubmit = async (values: FormData) => {
    setLoading(true);
    setErro(null);
    try {
      if (values.senha !== values.confirmarSenha) {
        setErro("As senhas não conferem.");
        return;
      }
      onNext(values);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="cadastro-grid-campos">
        <div className="cadastro-campo">
          <label htmlFor="razaoSocial" className="cadastro-rotulo">
            {data.tipoDocumento === "cpf" ? t("fields.full_name") : t("fields.company_name")}
          </label>
          <input
            id="razaoSocial"
            className="cadastro-entrada"
            autoComplete="organization"
            required
            {...register("razaoSocial", { required: true })}
          />
        </div>

        <div className="cadastro-campo">
          <label htmlFor="tipoDeConta" className="cadastro-rotulo">
            {t("fields.account_type")}
          </label>
          <select
            id="tipoDeConta"
            className="cadastro-selecao"
            {...register("tipoDeConta", { required: true })}
          >
            <option value="empresa">{t("options.company")}</option>
            <option value="parceiro">{t("options.partner")}</option>
          </select>
        </div>

        <div className="cadastro-campo col-span-2">
          <label htmlFor="email" className="cadastro-rotulo">
            {t("fields.email")}
          </label>
          <input
            id="email"
            type="email"
            required
            className="cadastro-entrada"
            autoComplete="email"
            {...register("email", { required: true })}
          />
        </div>

        <div className="cadastro-campo">
          <label htmlFor="telefoneCelular" className="cadastro-rotulo">
            {t("fields.phone")}
          </label>
          <input
            id="telefoneCelular"
            type="text"
            required
            className="cadastro-entrada"
            inputMode="numeric"
            {...register("telefoneCelular", {
              required: true,
              onChange: (e) => { e.target.value = onlyDigits(e.target.value); },
            })}
            onKeyDown={(e) => {
              const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
              if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) return;
              if (!/^\d$/.test(e.key)) e.preventDefault();
            }}
          />
        </div>

        <div className="cadastro-campo">
          <label className="cadastro-rotulo">&nbsp;</label>
        </div>

        <div className="cadastro-campo">
          <label htmlFor="senha" className="cadastro-rotulo">
            {t("fields.password")}
          </label>
          <input
            id="senha"
            type="password"
            required
            className="cadastro-entrada"
            autoComplete="new-password"
            {...register("senha", { required: true })}
          />
        </div>

        <div className="cadastro-campo">
          <label htmlFor="confirmarSenha" className="cadastro-rotulo">
            {t("fields.confirm_password")}
          </label>
          <input
            id="confirmarSenha"
            type="password"
            required
            className="cadastro-entrada"
            autoComplete="new-password"
            {...register("confirmarSenha", { required: true })}
          />
        </div>
      </div>

      {erro && <div className="cadastro-erro">{erro}</div>}

      <div className="cadastro-acoes">
        <Link to="/login" className="cadastro-btn-ghost">
          {t("buttons.go_to_login", "Go to login")}
        </Link>
        <button className="cadastro-btn-primario" type="submit" disabled={loading}>
          {t("buttons.continue")}
        </button>
      </div>
    </form>
  );
}
